import { FC, useEffect, useState } from 'react';
import { TextField, Button, Grid, SvgIcon, Select, MenuItem, FormControl, InputLabel, SelectChangeEvent } from '@mui/material';
import { Search } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import CustomPagination from '../../../../components/forms/Pagination';
import { Section } from '../../../../components/ui/Section';
import ModulesList from './ModulesList';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import { ModuleResponse } from '../../../../app/services/api/generated';

type Props = {
  onCreateNew: () => void;
};

const ModulesTab: FC<Props> = ({ onCreateNew }) => {
  const { t } = useTranslation('admin');
  const { getModules, getSystems } = useNswagClient();
  const [list, setList] = useState<ModuleResponse[]>([]);
  const [filteredList, setFilteredList] = useState<ModuleResponse[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [selectedSystem, setSelectedSystem] = useState<string>('');
  const [systems, setSystems] = useState<{ id: number; name: string }[]>([]);
  const pageSize = 20;

  const fetchSystems = async () => {
    try {
      const response = await getSystems();
      if (response?.data?.systems) {
        setSystems(
          response.data.systems.map((s: { id?: number; name?: string }) => ({
            id: s.id ?? 0,
            name: s.name ?? '',
          })),
        );
      }
    } catch (error) {
      console.error('Error fetching systems:', error);
    }
  };

  const fetchModules = async () => {
    setIsLoading(true);
    try {
      const response = await getModules();
      if (response?.data?.modules) {
        setList(response.data.modules);
      }
    } catch (error) {
      console.error('Error fetching modules:', error);
      setList([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSystems();
    fetchModules();
  }, []);

  useEffect(() => {
    let filtered = list;
    if (searchTerm) {
      filtered = filtered.filter(m =>
        (m.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (m.description || '').toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }
    if (selectedSystem) {
      filtered = filtered.filter(m => m.systemId?.toString() === selectedSystem);
    }
    setTotalCount(filtered.length);
    const start = currentPage * pageSize;
    setFilteredList(filtered.slice(start, start + pageSize));
  }, [list, searchTerm, selectedSystem, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearch();
    }
  };

  const onSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(0);
  };

  const handleSystemChange = (event: SelectChangeEvent<string>) => {
    setSelectedSystem(event.target.value);
    setCurrentPage(0);
  };

  const handleClear = () => {
    setSearchInput('');
    setSearchTerm('');
    setSelectedSystem('');
    setCurrentPage(0);
  };

  return (
    <Grid container
      spacing={5}>
      <Grid item
        lg={3}
        sm={6}
        xs={12}>
        <TextField
          label={t('modules.search')}
          placeholder={t('modules.searchPlaceholder')}
          value={searchInput}
          onChange={handleSearchInputChange}
          onKeyPress={handleSearchKeyPress}
          fullWidth
        />
      </Grid>
      <Grid item
        lg={3}
        sm={6}
        xs={12}>
        <FormControl fullWidth>
          <InputLabel shrink>
            {t('modules.filterBySystem')}
          </InputLabel>
          <Select
            value={selectedSystem}
            onChange={handleSystemChange}
            label={t('modules.filterBySystem')}
            displayEmpty
            notched
          >
            <MenuItem value="">
              {t('modules.allSystems')}
            </MenuItem>
            {systems.map((system) => (
              <MenuItem
                key={system.id}
                value={system.id.toString()}
              >
                {system.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button
          fullWidth
          onClick={handleClear}
          variant='secondary'
          size='lg'
        >
          {t('modules.clear')}
        </Button>
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button
          fullWidth
          onClick={onSearch}
          size='lg'
          startIcon={<SvgIcon><Search /></SvgIcon>}
        >
          {t('modules.search')}
        </Button>
      </Grid>
      <Grid item
        xs={12}>
        <Section>
          <ModulesList
            list={filteredList}
            isLoading={isLoading}
            totalCount={totalCount}
            onClear={handleClear}
            onCreateNew={onCreateNew}
          />
          {totalCount > pageSize && (
            <CustomPagination
              page={currentPage}
              setPage={handlePageChange}
              maxPages={Math.ceil(totalCount / pageSize)}
              breakpointForChangeDisplay={120000}
              useNoDataMessage={false}
            />
          )}
        </Section>
      </Grid>
    </Grid>
  );
};

export default ModulesTab;
